import { ReactElement } from "react";
import { createPortal } from "react-dom";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { Guid } from "@/Common/Models/Guid";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LinkButton } from "@/DesignSystem";
import { useFlowsheetFilterSettings } from "@/Flowsheets/Hooks/useFlowsheetFilterSettings";
import { FlowsheetFilterSettings } from "@/Flowsheets/Models/FlowsheetFilterSettings";
import { ResultsDurationType } from "@/Flowsheets/Models/ResultsDurationType";

import styles from "./FlowsheetFilterSettingsPopup.module.scss";

/**
 * Interface representing the props for the FlowsheetFilterSettingsPopup component.
 */
interface IFlowsheetFilterSettingsPopup {
    /**
     * Callback function triggered when the user confirms the settings.
     */
    onConfirm: (updatedSettings: FlowsheetFilterSettings, isMainFilter: boolean) => void;

    /**
     * Callback function triggered when the user closes the popup.
     */
    onClose: () => void;

    /*
    * Date of the encounter
    */
    encounterDate: Date;

    /**
     * Filter settings of the flowsheet.
     */
    flowsheetFilterSettings?: FlowsheetFilterSettings;

    /**
     * Unique identifier for the flowsheet.
     */
    flowsheetId: Guid;
}

/**
 * DatePicker component that renders a date picker input field with a button to set the current date.
 */
function DatePicker({
    id,
    date,
    encounterDate,
    onDateChange
}: Readonly<{
    id: string;
    date: DateTimeWithPrecision;
    encounterDate: Date;
    onDateChange: (date: DateTimeWithPrecision) => void;
}>): ReactElement {
    const { getText } = useTranslation();

    return (
        <>
            <TextboxControlWithDatePicker
                id={id}
                expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                textValue={date.text}
                dateValue={date.value}
                onChange={newDate => onDateChange(newDate)}
                maxDate={encounterDate}
                isInvalid={LuxonDateTimeParser.parse(date.value) > LuxonDateTimeParser.parse(encounterDate)}
            />
            <div className={styles.todayButtonContainer}>
                <LinkButton
                    label={getText("TodayLabel")}
                    size="sm"
                    variant="brand"
                    underline={false}
                    onClick={() => onDateChange(DateTimeWithPrecision.getToday())}
                />
            </div>
        </>
    );
}

/**
 * Checkbox component that renders a checkbox input field with a label.
 */
function Checkbox({
    checked,
    label,
    onChange
}: Readonly<{
    checked: boolean;
    label: string;
    onChange: (checked: boolean) => void;
}>): ReactElement {
    return (
        <label className={styles.checkboxLabel}>
            <input
                className={styles.radioOrCheckbox}
                type="checkbox"
                checked={checked}
                onChange={e => onChange(e.target.checked)}
            />
            <span>{label}</span>
        </label>
    );
}

/**
 * Radio component that renders a radio button input field with a label.
 */
function Radio({
    value,
    label,
    selectedRadio,
    onChange
}: Readonly<{
    value: ResultsDurationType;
    label: string;
    selectedRadio: ResultsDurationType;
    onChange: (value: ResultsDurationType) => void;
}>): ReactElement {
    return (
        <label className={styles.radioLabel}>
            <input
                type="radio"
                className={styles.radioOrCheckbox}
                name="displayOption"
                checked={selectedRadio === value}
                onChange={() => onChange(value)}
            />
            <span>{label}</span>
        </label>
    );
}

/**
 * FlowsheetFilterSettingsPopup component that displays a modal for configuring flowsheet filter settings.
 * @param param0 - The props for the FlowsheetFilterSettingsPopup component.
 * @returns A React element representing the FlowsheetFilterSettingsPopup component.
 */
function FlowsheetFilterSettingsPopup({
    onConfirm,
    onClose,
    encounterDate,
    flowsheetFilterSettings,
    flowsheetId
}: Readonly<IFlowsheetFilterSettingsPopup>): ReactElement {
    const { getText } = useTranslation();

    const {
        saveSettings,
        setSaveSettings,
        filterSettings,
        handleCheckboxChange,
        handleCareElementCheckboxChange,
        setResultsDuration,
        handleStartDateChange,
        handleEndDateChange,
        handleConfirm,
        isStartDateInvalid,
        isEndDateInvalid
    } = useFlowsheetFilterSettings(flowsheetFilterSettings, flowsheetId, onConfirm);

    const careComponentFilterSetting = filterSettings?.CareComponentFilterSettings?.find(x => x.IsEnabled);
    const isCareComponentFilterSetting = careComponentFilterSetting?.CareElements?.length > 0;
    const careComponentName = isCareComponentFilterSetting ? careComponentFilterSetting.Name : undefined;
    const effectiveSettings = isCareComponentFilterSetting && careComponentFilterSetting
        ? careComponentFilterSetting
        : filterSettings;

    const allChecked = careComponentFilterSetting?.CareElements.every(element => element.IsEnabled) ?? false;

    const toggleAllCareElements = (checked: boolean): void => {
        if (!careComponentFilterSetting)
            return;

        careComponentFilterSetting.CareElements.forEach(element => {
            handleCareElementCheckboxChange(careComponentFilterSetting.Name, element.Key, checked);
        });
    };

    return createPortal(
        <Modal
            header={(
                <div className={isCareComponentFilterSetting ? styles.headerGridLayout4 : styles.headerGridLayout3}>
                    <div className={styles.headerRow && styles.headerLabel}>{getText("DateLabel")}</div>
                    {isCareComponentFilterSetting && (
                        <div className={styles.headerRow}>
                            <Checkbox
                                checked={allChecked}
                                label={getText("Flowsheets_Settings_CareElementLabel")}
                                onChange={toggleAllCareElements}
                            />
                        </div>
                    )}
                    <div className={styles.headerRow && styles.headerLabel}>{getText("Flowsheets_Settings_CareElementDisplayLabel")}</div>
                    <div className={styles.headerRow && styles.headerLabel}>{getText("SettingsLabel")}</div>
                </div>
            )}

            showX
            disableAutoScroll
            disableOverlayClose
            showOverlay
            onClose={onClose}
            width={isCareComponentFilterSetting ? 800 : 700}
            footer={(
                <div className={styles.flowsheetPopupFooter}>
                    {!isCareComponentFilterSetting && (
                        <div className={styles.saveSettingsContainer}>
                            <Checkbox
                                checked={saveSettings}
                                label={getText("RememberUserSettings")}
                                onChange={setSaveSettings}
                            />
                        </div>
                    )}
                    <ButtonGroup>
                        <ButtonControl
                            label={cacheStore.GetString("CancelLabel")}
                            variant="neutral"
                            onClick={onClose}
                        />
                        <ButtonControl
                            variant="brand"
                            onClick={handleConfirm}
                            label={cacheStore.GetString("OKLabel")}
                            disabled={isStartDateInvalid || isEndDateInvalid}
                        />
                    </ButtonGroup>
                </div>
            )}
        >
            <div className={isCareComponentFilterSetting ? styles.gridLayout4 : styles.gridLayout3}>
                <div className={styles.column}>
                    <DatePicker
                        id="settingsStartDate"
                        date={new DateTimeWithPrecision(
                            effectiveSettings?.StartDate ?? "",
                            effectiveSettings?.StartDate ?? "",
                            LifeStageDatePrecisionEnum.Day
                        )}
                        encounterDate={encounterDate}
                        onDateChange={date => handleStartDateChange(date, encounterDate, careComponentName)}
                    />
                    <DatePicker
                        id="settingsEndDate"
                        date={new DateTimeWithPrecision(
                            effectiveSettings?.EndDate ?? "",
                            effectiveSettings?.EndDate ?? "",
                            LifeStageDatePrecisionEnum.Day
                        )}
                        encounterDate={encounterDate}
                        onDateChange={date => handleEndDateChange(date, encounterDate, careComponentName)}
                    />
                </div>

                {isCareComponentFilterSetting && (
                    <div className={styles.column}>
                        {careComponentFilterSetting.CareElements.map(config => (
                            <Checkbox
                                key={config.Key}
                                checked={config.IsEnabled}
                                label={getText(`Flowsheets_${careComponentFilterSetting.Name}Label_${config.LabelKey}`)}
                                onChange={checked => handleCareElementCheckboxChange(
                                    careComponentFilterSetting.Name,
                                    config.Key,
                                    checked
                                )}
                            />
                        ))}
                    </div>
                )}

                <div className={styles.column}>
                    <Checkbox
                        checked={effectiveSettings.OutsideTreatmentTarget}
                        label={getText("Flowsheets_Settings_OutsideTreatmentTargetLabel")}
                        onChange={checked => handleCheckboxChange("OutsideTreatmentTarget", checked, careComponentName)}
                    />
                    <Checkbox
                        checked={effectiveSettings.OutsideTreatmentFrequency}
                        label={getText("Flowsheets_Settings_OutsideTreatmentFrequencyLabel")}
                        onChange={checked => handleCheckboxChange("OutsideTreatmentFrequency", checked, careComponentName)}
                    />
                </div>

                <div className={styles.column}>
                    <Radio
                        value={ResultsDurationType.Latest}
                        label={getText("Flowsheets_Settings_DisplayLatestEntryLabel")}
                        selectedRadio={effectiveSettings.ResultsDurationType}
                        onChange={value => setResultsDuration(value, careComponentName)}
                    />
                    <Radio
                        value={ResultsDurationType.Last2}
                        label={getText("Flowsheets_Settings_DisplayLastTwoEntriesLabel")}
                        selectedRadio={effectiveSettings.ResultsDurationType}
                        onChange={value => setResultsDuration(value, careComponentName)}
                    />
                    <Radio
                        value={ResultsDurationType.Last3}
                        label={getText("Flowsheets_Settings_DisplayLastThreeEntriesLabel")}
                        selectedRadio={effectiveSettings.ResultsDurationType}
                        onChange={value => setResultsDuration(value, careComponentName)}
                    />
                    <Radio
                        value={ResultsDurationType.All}
                        label={getText("Flowsheets_Settings_DisplayAllLabel")}
                        selectedRadio={effectiveSettings.ResultsDurationType}
                        onChange={value => setResultsDuration(value, careComponentName)}
                    />
                </div>
            </div>
        </Modal>,
        document.body
    );
}

export { FlowsheetFilterSettingsPopup };
